import React, { useState, useEffect } from 'react';
import { listarUsuarios, cambiarEstadoUsuario, eliminarUsuarioAdmin, ajustarCreditosUsuario } from '../../services/usuario';
import '../../styles/admin/AdminUsuarios.css';

function AdminUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [creditosModal, setCreditosModal] = useState('');
  const [accionModal, setAccionModal] = useState('');

  const cargarUsuarios = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listarUsuarios(skip, limit);
      setUsuarios(data);
      setTotalUsuarios(data.length);
    } catch (err) {
      setError('Error al cargar usuarios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
  }, [skip, limit]);

  const handleCambiarEstado = async (usuarioId, activo) => {
    try {
      await cambiarEstadoUsuario(usuarioId, activo);
      cargarUsuarios();
    } catch (err) {
      setError('Error al cambiar estado: ' + err.message);
    }
  };

  const handleEliminar = async (usuarioId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario? Esta acción no se puede deshacer.')) return;
    try {
      await eliminarUsuarioAdmin(usuarioId);
      cargarUsuarios();
    } catch (err) {
      setError('Error al eliminar usuario: ' + err.message);
    }
  };

  const handleAjustarCreditos = async (usuarioId) => {
    const creditos = parseInt(creditosModal);
    if (isNaN(creditos)) {
      setError('Ingresa un número válido');
      return;
    }
    try {
      await ajustarCreditosUsuario(usuarioId, creditos);
      setShowModal(false);
      setCreditosModal('');
      cargarUsuarios();
    } catch (err) {
      setError('Error al ajustar créditos: ' + err.message);
    }
  };

  const abrirModalCreditos = (usuario) => {
    setSelectedUser(usuario);
    setCreditosModal('');
    setAccionModal('creditos');
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="admin-usuarios-loading">Cargando usuarios...</div>;
  }

  if (error) {
    return (
      <div className="admin-usuarios-error">
        <p>{error}</p>
        <button onClick={cargarUsuarios}>Reintentar</button>
      </div>
    );
  }

  return (
    <div className="admin-usuarios-container">
      <div className="admin-usuarios-header">
        <h1>👥 Gestión de Usuarios</h1>
        <div className="admin-usuarios-controls">
          <span>Mostrando {skip + 1} - {Math.min(skip + limit, totalUsuarios)} de {totalUsuarios}</span>
          <div className="admin-usuarios-paginacion">
            <button 
              onClick={() => setSkip(Math.max(0, skip - limit))} 
              disabled={skip === 0}
            >
              ← Anterior
            </button>
            <button 
              onClick={() => setSkip(skip + limit)} 
              disabled={skip + limit >= totalUsuarios}
            >
              Siguiente →
            </button>
          </div>
        </div>
      </div>

      <div className="admin-usuarios-table-wrapper">
        <table className="admin-usuarios-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Créditos</th>
              <th>Estado</th>
              <th>Fecha Registro</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.length === 0 ? (
              <tr>
                <td colSpan="8" className="admin-usuarios-empty">No hay usuarios registrados</td>
              </tr>
            ) : (
              usuarios.map((usuario) => (
                <tr key={usuario.id} className={!usuario.activo ? 'inactivo' : ''}>
                  <td>{usuario.id}</td>
                  <td>{usuario.nombre}</td>
                  <td>{usuario.email}</td>
                  <td>
                    <span className={`rol-badge ${usuario.rol === 'admin' ? 'admin' : 'usuario'}`}>
                      {usuario.rol === 'admin' ? '👑 Admin' : '👤 Usuario'}
                    </span>
                  </td>
                  <td>{usuario.creditos_prueba}</td>
                  <td>
                    <span className={`estado-badge ${usuario.activo ? 'activo' : 'inactivo'}`}>
                      {usuario.activo ? '✅ Activo' : '❌ Inactivo'}
                    </span>
                  </td>
                  <td>{formatDate(usuario.fecha_registro)}</td>
                  <td>
                    <div className="acciones-buttons">
                      <button 
                        className="btn-cambiar-estado"
                        onClick={() => handleCambiarEstado(usuario.id, !usuario.activo)}
                        title={usuario.activo ? 'Desactivar' : 'Activar'}
                      >
                        {usuario.activo ? '🔴' : '🟢'}
                      </button>
                      <button 
                        className="btn-creditos"
                        onClick={() => abrirModalCreditos(usuario)}
                        title="Ajustar créditos"
                      >
                        💰
                      </button>
                      <button 
                        className="btn-eliminar"
                        onClick={() => handleEliminar(usuario.id)}
                        title="Eliminar usuario"
                        disabled={usuario.rol === 'admin'}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL DE AJUSTAR CRÉDITOS */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajustar créditos</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Usuario: <strong>{selectedUser.nombre}</strong> ({selectedUser.email})</p>
              <p>Créditos actuales: <strong>{selectedUser.creditos_prueba}</strong></p>
              <div className="modal-field">
                <label>Ingresa la cantidad a sumar o restar (ej: 10 o -5)</label>
                <input
                  type="number"
                  value={creditosModal}
                  onChange={(e) => setCreditosModal(e.target.value)}
                  placeholder="Ej: 10"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-modal-confirm" onClick={() => handleAjustarCreditos(selectedUser.id)}>
                Ajustar créditos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsuarios;